import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatDate,
} from '../components/ui';

interface Form {
  id: string;
  name: string;
  fields: FormField[];
  createdAt: string;
}

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
  label: string;
  required: boolean;
  options?: string[];
}

export function FormsPage() {
  const [editing, setEditing] = useState<Form | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['forms'],
    queryFn: () => api.get<{ data: Form[] }>('/forms').catch(() => ({ data: [] })),
  });

  const forms = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Forms"
        subtitle="Capture leads, registrations, surveys"
        action={<Button onClick={() => setShowCreate(true)}>+ New form</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : forms.length === 0 ? (
        <Card>
          <EmptyState
            title="No forms yet"
            description="Create a form, embed it on your site, watch responses come in."
            action={<Button onClick={() => setShowCreate(true)}>+ New form</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {forms.map((f) => (
            <Card key={f.id} onClick={() => setEditing(f)}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{f.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    {f.fields?.length ?? 0} fields · {formatDate(f.createdAt)}
                  </div>
                </div>
                <Badge color="gray">Embeddable</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <FormEditor
          form={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['forms'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function FormEditor({
  form,
  onClose,
  onSaved,
}: {
  form: Form | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(form?.name ?? '');
  const [fields, setFields] = useState<FormField[]>(form?.fields ?? []);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = { name, fields };
      return form ? api.patch(`/forms/${form.id}`, body) : api.post('/forms', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/forms/${form!.id}`),
    onSuccess: onSaved,
  });

  function addField() {
    setFields([
      ...fields,
      { id: `f_${Math.random().toString(36).slice(2, 10)}`, type: 'text', label: 'New field', required: false },
    ]);
  }

  function updateField(id: string, patch: Partial<FormField>) {
    setFields(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  function removeField(id: string) {
    setFields(fields.filter((f) => f.id !== id));
  }

  const embedCode = form
    ? `<script src="${(import.meta.env.VITE_API_URL as string | undefined) ?? 'https://frontdesk-api.blancorunning.workers.dev'}/embed/forms/${form.id}.js" async></script>`
    : null;

  return (
    <Modal onClose={onClose} title={form ? 'Edit form' : 'New form'} width={680}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Form name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            Fields
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {fields.map((f) => (
              <Card key={f.id} style={{ padding: 'var(--space-2)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <Input value={f.label} onChange={(v) => updateField(f.id, { label: v })} placeholder="Label" />
                  <Select
                    value={f.type}
                    onChange={(v) => updateField(f.id, { type: v as FormField['type'] })}
                    options={[
                      { value: 'text', label: 'Text' },
                      { value: 'email', label: 'Email' },
                      { value: 'phone', label: 'Phone' },
                      { value: 'textarea', label: 'Long text' },
                      { value: 'select', label: 'Select' },
                      { value: 'checkbox', label: 'Checkbox' },
                    ]}
                  />
                  <label style={{ fontSize: 'var(--text-xs)', display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="checkbox" checked={f.required} onChange={(e) => updateField(f.id, { required: e.target.checked })} />
                    Required
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeField(f.id)}>
                    ×
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          <Button variant="secondary" onClick={addField} style={{ marginTop: 'var(--space-2)' }}>
            + Add field
          </Button>
        </div>

        {embedCode && (
          <Field label="Embed code">
            <pre
              style={{
                background: 'var(--color-surface-secondary)',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                overflowX: 'auto',
              }}
            >
              {embedCode}
            </pre>
          </Field>
        )}

        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {form ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
